import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import {Option} from '../../../../ui/components/common/Select';
import {selectedPresentationSelector} from '../../../../store/selectors/presentation.selectors';
import {IElementQuestion} from '../../../../incase-domain/elements/question/question';
import {IElementQuestionYesNo} from '../../../../incase-domain/elements/question/questionYesNo';
import {IQuestionReportElement} from '../../../../incase-domain/elements/question/questionReportElement';
import {IElementMultiSelect} from '../../../../incase-domain/elements/question/multiSelect';
import {IElementSequenceQuestion} from '../../../../incase-domain/elements/question/questionSequence';
import {generateErrorMessages, getErrorClasses} from '../../../../utils/validation';
import ElementShadow from '../common/elementShadow';
import FontSelect from '../common/fontSelect';
import {TABS} from '../../form/part/tabs';
import SectionTitle from '../../../elements/common/SectionTitle';
import Label from '../../../../incase-domain/components/label/Label';
import DropdownMenuCompact from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import Button from '../../../../incase-domain/components/button/Button';
import {getQuestions} from './utils';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';

interface HighlightedQuestionReportElementComponentProps {
    errors: IValidationErrorObject | null;
    activeTab: string;
}

function HighlightedQuestionReportElementComponent(props: HighlightedQuestionReportElementComponentProps): ReactElement {
    const {errors, activeTab} = props;
    const highlightedElement = useHighlightedElement() as IQuestionReportElement;
    const {questionElementId} = highlightedElement;
    const {t} = useTranslation();
    const updateHighlightedElement = useSetHighlightedElement();
    const presentation = useSelector(selectedPresentationSelector);

    const questions = getQuestions(presentation);

    const selectedQuestion = questions.find((q) => q.id === questionElementId);

    function mapToOption(question: IElementQuestion | IElementQuestionYesNo | IElementMultiSelect | IElementSequenceQuestion): Option {
        return {label: question.question, value: question.id};
    }

    if (activeTab === TABS.STYLE.id) {
        return <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />;
    }

    if (activeTab === TABS.ADVANCED.id) {
        return <></>;
    }

    const chooseQuestionItems = [
        questions.map((el) => ({
            label: el.question,
            onClick: () => updateHighlightedElement.single('questionElementId', el.id),
        })),
    ];

    return (
        <>
            <div className="l-group--form">
                <div className={`l-group__item ${getErrorClasses(errors, 'questionElementId')}`}>
                    <Label>{t('designSidepanel.chooseQuestion')}</Label>
                    <DropdownMenuCompact
                        width="full"
                        key={selectedQuestion && selectedQuestion.question}
                        className="c-sidepanel__dropdown"
                        items={chooseQuestionItems}
                    >
                        <Button
                            fullWidth
                            size="xs"
                            trailing="solar:alt-arrow-down-linear"
                            variant="soft"
                            className="c-sidepanel__dropdown_button"
                        >
                            {(selectedQuestion && mapToOption(selectedQuestion))?.label || t('designSidepanel.chooseQuestion')}
                        </Button>
                    </DropdownMenuCompact>

                    {generateErrorMessages(errors, 'questionElementId')}
                </div>
            </div>

            <div className="l-group--form">
                <SectionTitle label={t('designSidepanel.style')} />
                <FontSelect highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
            </div>
        </>
    );
}

export default HighlightedQuestionReportElementComponent;
